"use client";

import { c as _c } from "react/compiler-runtime";
import { BadgePercent } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDayMonthYear } from "@/shared/lib/format/dates";
import { fmtUzs } from "@/shared/lib/format/numbers";
import { cn } from "@/shared/lib/utils";
import { Badge } from "@/shared/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/shared/ui/hover-card";
export type PromoDetails = {
  price: number | null;
  promo_price: number | null;
  promotion_text: string | null;
  promotion_type: string | null;
  buy_quantity: number | null;
  bonus_quantity: number | null;
  bonus_medicine_raw_name: string | null;
  promo_start_date: string | null;
  promo_end_date: string | null;
};
type Props = {
  promo: PromoDetails;
  intlLocale: string;
};
const titleCase = (s: string) => s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
export function PromoDetailsContent(t0) {
  const $ = _c(24);
  const {
    promo,
    intlLocale
  } = t0;
  const {
    t
  } = useTranslation();
  const hasCombo = promo.buy_quantity != null && promo.bonus_quantity != null;
  let t1;
  if ($[0] !== hasCombo || $[1] !== intlLocale || $[2] !== promo.bonus_medicine_raw_name || $[3] !== promo.bonus_quantity || $[4] !== promo.buy_quantity || $[5] !== promo.price || $[6] !== promo.promo_end_date || $[7] !== promo.promo_price || $[8] !== promo.promo_start_date || $[9] !== promo.promotion_text || $[10] !== promo.promotion_type || $[11] !== t) {
    const discountPct = promo.promo_price != null && promo.price != null && promo.price > 0 && promo.promo_price < promo.price ? Math.round((1 - promo.promo_price / promo.price) * 100) : null;
    let t2;
    if ($[13] !== promo.promotion_text || $[14] !== promo.promotion_type || $[15] !== t) {
      t2 = promo.promotion_text ?? promo.promotion_type ?? t("wholesaleOffers.promo.popup.defaultTitle");
      $[13] = promo.promotion_text;
      $[14] = promo.promotion_type;
      $[15] = t;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    const title = t2;
    const bonusName = promo.bonus_medicine_raw_name ? titleCase(promo.bonus_medicine_raw_name) : null;
    const showDates = promo.promo_start_date != null || promo.promo_end_date != null;
    const startStr = promo.promo_start_date ? formatDayMonthYear(promo.promo_start_date, intlLocale) : "\u2014";
    const endStr = promo.promo_end_date ? formatDayMonthYear(promo.promo_end_date, intlLocale) : "\u2014";
    let t3;
    if ($[17] !== title) {
      t3 = <div className="text-foreground text-sm font-semibold">{title}</div>;
      $[17] = title;
      $[18] = t3;
    } else {
      t3 = $[18];
    }
    let t4;
    if ($[19] !== hasCombo || $[20] !== promo.bonus_quantity || $[21] !== promo.buy_quantity || $[22] !== t) {
      t4 = hasCombo ? <div className="text-foreground text-sm">{t("wholesaleOffers.promo.popup.combo", {
          buy: promo.buy_quantity,
          bonus: promo.bonus_quantity
        })}</div> : null;
      $[19] = hasCombo;
      $[20] = promo.bonus_quantity;
      $[21] = promo.buy_quantity;
      $[22] = t;
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    t1 = <>{t3}{promo.promo_price != null && promo.price != null && promo.promo_price !== promo.price ? <div className="space-y-0.5"><div className="text-muted-foreground text-xs">{t("wholesaleOffers.promo.popup.priceLabel")}</div><div className="text-muted-foreground text-xs tabular-nums line-through">{fmtUzs(promo.price)}</div><div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5"><span className="text-foreground text-base font-semibold tabular-nums">{fmtUzs(promo.promo_price)}</span>{discountPct != null ? <span className="text-xs whitespace-nowrap text-emerald-700 dark:text-emerald-300">{t("wholesaleOffers.promo.popup.discount", {
              pct: discountPct
            })}</span> : null}</div></div> : null}{t4}{bonusName ? <div className="text-foreground text-sm">{t("wholesaleOffers.promo.popup.bonusMedicine", {
          name: bonusName
        })}</div> : null}{showDates ? <div className="text-muted-foreground text-xs">{t("wholesaleOffers.promo.popup.validFromThrough", {
          start: startStr,
          end: endStr
        })}</div> : null}</>;
    $[0] = hasCombo;
    $[1] = intlLocale;
    $[2] = promo.bonus_medicine_raw_name;
    $[3] = promo.bonus_quantity;
    $[4] = promo.buy_quantity;
    $[5] = promo.price;
    $[6] = promo.promo_end_date;
    $[7] = promo.promo_price;
    $[8] = promo.promo_start_date;
    $[9] = promo.promotion_text;
    $[10] = promo.promotion_type;
    $[11] = t;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  return t1;
}
export function PromoBadge(t0) {
  const $ = _c(18);
  const {
    promo,
    intlLocale
  } = t0;
  const {
    t
  } = useTranslation();
  const hasCombo = promo.buy_quantity != null && promo.bonus_quantity != null;
  let t1;
  if ($[0] !== promo.price || $[1] !== promo.promo_price) {
    t1 = promo.promo_price != null && promo.price != null && promo.price > 0 && promo.promo_price < promo.price ? Math.round((1 - promo.promo_price / promo.price) * 100) : null;
    $[0] = promo.price;
    $[1] = promo.promo_price;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const discountPct = t1;
  const compactLabel = hasCombo ? `${promo.buy_quantity}+${promo.bonus_quantity}` : discountPct != null ? `−${discountPct}%` : null;
  let t2;
  if ($[3] !== t) {
    t2 = t("wholesaleOffers.promo.popup.ariaLabel");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("h-5 cursor-default gap-1 px-1.5 text-xs leading-none [&>svg]:size-3!");
    t4 = <BadgePercent aria-hidden={true} />;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[7] !== compactLabel) {
    t5 = compactLabel ? <span>{compactLabel}</span> : null;
    $[7] = compactLabel;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t5) {
    t6 = <HoverCardTrigger asChild={true}><Badge variant="secondary" tabIndex={0} role="button" aria-label={t2} onClick={_temp} className={t3}>{t4}{t5}</Badge></HoverCardTrigger>;
    $[9] = t2;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== intlLocale || $[13] !== promo) {
    t7 = <HoverCardContent align="end" sideOffset={4} className="w-80 space-y-2"><PromoDetailsContent promo={promo} intlLocale={intlLocale} /></HoverCardContent>;
    $[12] = intlLocale;
    $[13] = promo;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <HoverCard openDelay={120} closeDelay={80}>{t6}{t7}</HoverCard>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function _temp(e) {
  return e.stopPropagation();
}